<script setup>
import VisibilityRadialChart from "@/components/seo-score/visibility-chart/VisibilityRadialChart.vue";
import OptimisationScoreChart from "@/components/seo-score/partials/OptimisationScoreChart.vue";
import StatBox from "@/components/seo-score/partials/StatBox.vue";
import StatBoxSkeleton from "@/components/seo-score/partials/StatBoxSkeleton.vue";
import ScoreInfo from "@/components/seo-score/partials/ScoreInfo.vue";
import ImproveScoreInfo from "@/components/seo-score/partials/ImproveScoreInfo.vue";
import GoogleConnection from "@/components/seo-score/partials/GoogleConnection.vue";
import SquareIconButton from "@/components/global-themed/buttons/SquareIconButton.vue";
import {useGoogleAccounts} from "@/composables/useGoogleAccounts.js";
import {useSeoScore, refreshScore} from "@/composables/useSeoScore";
import {watch} from "vue";

const {
  analyticsStatus,
  searchConsoleStatus,
  businessProfileStatus,
  analyticsUri,
  searchConsoleUri,
  businessProfileUri,
} = useGoogleAccounts();

const {isLoading, stats, visibilityScoreStats, visibilityScore} = useSeoScore();
const {mutate: refresh, isPending} = refreshScore();

const handleRefresh = () => {
  window.mixpanel('SEO Score: Manually Refreshed');
  refresh();
};

watch(visibilityScore, (newVal) => {
  if (!newVal) {
    return;
  }

  window.mixpanel('SEO Score: Viewed Score', { 
    visibilityScore: newVal,
  });
}, { immediate: true });

</script>

<template>
  <div class="tailwind tw-flex tw-flex-col tw-gap-y-8">
    <div class="tw-rounded-xl tw-bg-white tw-p-6 lg:tw-p-10 tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-6 tw-mb-4">
      <div class="tw-shrink-0">
        <SquareIconButton
          v-tooltip="{
            content: 'Refresh score',
            theme: 'dark-blue-tooltip',
            placement: 'bottom',
          }"
          size="small"
          variant="primary"
          class="tw-p-3 tw-border-2 tw-rounded-xl tw-max-w-11"
          :loading="isPending"
          :disabled="isPending"
          @click="handleRefresh"
        >
          <svg-vue
            class="tw-w-4 tw-flex-shrink-0"
            icon="icons/refresh"
          />
        </SquareIconButton>
      </div>

      <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-[minmax(0,1fr)_440px] tw-gap-y-6 lg:tw-gap-x-10 lg:tw-gap-y-10 tw-flex-grow tw-min-w-0">
        <div class="tw-row-start-1 tw-w-full tw-min-w-0">
          <div class="tw-w-full tw-aspect-[4/3] lg:tw-aspect-auto lg:tw-min-h-[280px]">
            <VisibilityRadialChart
              :value="visibilityScoreStats.score"
              :industry-average="visibilityScoreStats.industryAverage"
              :percentage-change="visibilityScoreStats.percentageChange"
              :loading="isLoading"
            />
          </div>
        </div>

        <div class="tw-row-start-2 lg:tw-row-start-1 lg:tw-col-start-2 lg:tw-row-span-2 tw-flex tw-flex-col tw-justify-between tw-w-full tw-min-w-0 tw-space-y-6">
          <div class="tw-flex tw-flex-col tw-justify-between tw-gap-y-6">
            <OptimisationScoreChart />

            <template v-if="isLoading">
              <StatBoxSkeleton
                v-for="i in 2"
                :key="`skeleton-${i}`"
              />
            </template>

            <template v-else>
              <StatBox
                v-for="stat in stats"
                :key="stat.name"
                :stat="stat"
              />

              <div
                v-if="stats.length < 2"
                class="tw-border tw-border-info tw-rounded-xl tw-p-5 tw-bg-info-100"
              >
                <p class="tw-text-sm tw-text-neutral-900 tw-mb-0">
                  Connect your Google accounts to see how customers are finding your business on Google. This unlocks
                  additional data like website visitors, search visibility, and calls so we can give you more accurate
                  insights.
                </p>
              </div>
            </template>
          </div>

          <ImproveScoreInfo :visibility-score="visibilityScoreStats.score" />
          <ScoreInfo />
        </div>

        <div class="tw-row-start-3 lg:tw-row-start-2 lg:tw-col-start-1 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-6">
          <GoogleConnection
            :status="analyticsStatus"
            name="Analytics"
            icon="icons/google-analytics-4"
            :connection-uri="analyticsUri"
          />
          <GoogleConnection
            :status="searchConsoleStatus"
            name="Search Console"
            icon="icons/google-search-console"
            :connection-uri="searchConsoleUri"
          />
          <GoogleConnection
            :status="businessProfileStatus"
            name="Business Profile"
            icon="icons/google-my-business-logo"
            :connection-uri="businessProfileUri"
          />
        </div>
      </div>
    </div>
  </div>
</template>
